import { FieldValues, useController, UseControllerProps } from 'react-hook-form';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters } from 'store/useBusinessToolStore/selectors';

import { getYearsPeriod } from 'helpers/formatPeriods';
import { isPerformanceInvestmentPeriodsObject } from 'helpers/objects';

import { TextInput } from '../../TextInput';
import { ITextFieldProps } from '../../TextInput/TextInput/types';

import styles from './NumberInputControlled.module.scss';

const MAX_TOTAL_LENGTH = 19; /* It also count length for ',' from formatter */
export const DEFAULT_VALUE = '0';

export interface INumberInputControlledProps<T extends FieldValues>
  extends UseControllerProps<T>, Omit<ITextFieldProps, 'type' | 'defaultValue' | 'name'> {
  maxValue?: number;
  decimalScale?: number;
  defaultNaNValue?: string;
}

export const NumberInputControlled = <T extends FieldValues>(props: INumberInputControlledProps<T>) => {
  const {
    name,
    control,
    rules,
    defaultValue,
    readOnly,
    maxValue,
    maxLength = MAX_TOTAL_LENGTH,
    allowNegative = false,
    decimalScale = 2,
    defaultNaNValue = '—',
    autoComplete = 'off',
    ...restProps
  } = props;

  const { field, fieldState } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const { ref, value, onChange, ...restField } = field;
  const { error } = fieldState;

  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const { period } = businessPlanChapters.GLOBAL;

  const checkIsAllowed = ({ value }: NumberFormatValues) => {
    const isBelowMaxValue = maxValue ? Number(value) <= maxValue : true;

    return isBelowMaxValue;
  };

  const handleChangeValue = (value: string) => {
    // TODO
    // this code is needed because when we clear the input in product's card it doesn't change calculated
    // value in the form, so we need to set the input value to '0' to achieve this behavior

    if (!value) {
      onChange(defaultValue ?? DEFAULT_VALUE);
    } else {
      onChange(value);
    }
  };

  const safeValue = (value: unknown): string | number | undefined => {
    if (isPerformanceInvestmentPeriodsObject(value)) {
      return safeValue(value[getYearsPeriod(period)]);
    }
    if (!value) {
      return '';
    } else if (typeof value === 'string' && value !== DEFAULT_VALUE) {
      return Number(value);
    } else if (typeof value === 'number') {
      return value;
    } else {
      return '';
    }
  };

  if (
    (restProps.value ?? value) === 'NaN' ||
    (isPerformanceInvestmentPeriodsObject(value) && value[getYearsPeriod(period)] === 'NaN')
  ) {
    return <TextInput readOnly value={defaultNaNValue} {...props} />;
  }

  if (name.includes('paybackPeriod') && !Number(restProps.value ?? value)) {
    return <TextInput readOnly value={'0'} {...props} />;
  }

  return (
    <NumericFormat
      {...props}
      autoComplete={autoComplete}
      isAllowed={checkIsAllowed}
      maxLength={maxLength}
      inputRef={ref}
      customInput={TextInput}
      inputProps={{
        readOnly,
        className: styles.numberInput,
        autoComplete,
      }}
      onValueChange={({ value }) => handleChangeValue(value)}
      fieldError={error}
      thousandSeparator={true}
      decimalSeparator="."
      decimalScale={decimalScale}
      allowNegative={allowNegative}
      {...restField}
      {...restProps}
      value={safeValue(restProps.value ?? value)}
      fieldClassName={styles.wrapper}
    />
  );
};
